import { Component, computed, input, output, signal } from '@angular/core';

import { ChatItem } from '../../home.models';
import { CommonModule } from '@angular/common';
import { UserHoverCardComponent } from '../user-hover-card/user-hover-card.component';

@Component({
  selector: 'app-chat-sidebar',
  imports: [CommonModule, UserHoverCardComponent],
  templateUrl: './chat-sidebar.component.html',
  styleUrl: './chat-sidebar.component.scss'
})
export class ChatSidebarComponent {
  readonly chats = input.required<ChatItem[]>();
  readonly selectedChatName = input.required<string>();
  readonly userProfilesById = input<Record<string, { name: string; email: string | null; avatarUrl: string | null }>>({});
  // Consumed by Home to switch the main panel into compose mode.
  readonly startNewChat = output<void>();
  // Consumed by Home to set the active chat/channel.
  readonly selectChat = output<ChatItem>();
  protected readonly showSearch = signal(false);
  protected readonly unreadOnly = signal(false);
  protected readonly searchQuery = signal('');

  // Local sidebar filter state only; the actual source chat list is owned by Home.
  protected readonly filteredChats = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const chats = this.unreadOnly()
      ? this.chats().filter((chat) => (chat.unread ?? 0) > 0)
      : this.chats();

    if (!query) {
      return chats;
    }

    return chats.filter((chat) => chat.name.toLowerCase().includes(query));
  });

  protected toggleSearch(): void {
    this.showSearch.update((value) => !value);
    if (!this.showSearch()) {
      this.searchQuery.set('');
    }
  }

  protected onSearchInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement | null;
    this.searchQuery.set(inputElement?.value ?? '');
  }

  protected toggleUnreadOnly(): void {
    this.unreadOnly.update((value) => !value);
  }

  protected onStartNewChat(): void {
    this.startNewChat.emit();
  }

  protected onSelectChat(chat: ChatItem): void {
    this.selectChat.emit(chat);
  }

  protected getChatHoverName(chat: ChatItem): string {
    const userId = chat.participantUserId?.trim();
    if (!userId) {
      return chat.name;
    }

    return this.userProfilesById()[userId]?.name?.trim() || chat.name;
  }

  protected getChatHoverEmail(chat: ChatItem): string | null {
    const userId = chat.participantUserId?.trim();
    if (!userId) {
      return null;
    }

    return this.userProfilesById()[userId]?.email?.trim() || null;
  }

  protected getChatHoverAvatarUrl(chat: ChatItem): string | null {
    const userId = chat.participantUserId?.trim();
    if (!userId) {
      return chat.avatarUrl ?? null;
    }

    return this.userProfilesById()[userId]?.avatarUrl ?? chat.avatarUrl ?? null;
  }
}
